import {
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from "recharts";
import type {
  CampaignImpactMarker,
  InsightsDailyRevenuePoint,
} from "@shared-core/data/api-types";
import {
  formatGbpCompact,
  niceAxisMax,
  projectCampaignMarkers,
  projectDailyRevenue,
  totalRevenue,
  type CampaignMarkerDatum,
  type DailyRevenueDatum,
} from "@shared-core/domain/insights-campaign-impact";

// Booked revenue trend (trailing window) with each campaign's send date
// marked on the timeline — so a rep or the marketing team can eyeball
// whether a revenue peak follows shortly after a send. Booked basis only
// (the marketing manager's ask: one clear trend line, not a toggle).
//
// A line, not the Daily tab's bars: the ask was literally "a line graph of
// how sales are doing" with sends plotted on it, and a continuous line is
// what makes a peak-after-a-marker legible across ~90 days.
//
// Server composes, client renders: this component only projects the fields
// the endpoint already returns; it performs no aggregation.

const REVENUE_COLOR = "var(--color-chart-1)";
const MARKER_COLOR = "#b45309";

export interface CampaignImpactChartProps {
  days: InsightsDailyRevenuePoint[];
  campaignMarkers: CampaignImpactMarker[];
}

export function CampaignImpactChart({
  days,
  campaignMarkers,
}: CampaignImpactChartProps) {
  const data = projectDailyRevenue(days, "booked");
  const { plotted: markers, unplotted } = projectCampaignMarkers(
    campaignMarkers,
    days,
  );

  if (days.length === 0) {
    return (
      <div className="border-border bg-surface text-muted-foreground rounded-lg border p-12 text-center text-sm">
        No revenue data for this period yet.
      </div>
    );
  }

  const revenueMax = niceAxisMax(Math.max(0, ...data.map((d) => d.revenue)));

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-foreground text-base font-semibold">
          Booked revenue &amp; campaign sends
        </h2>
        <p className="text-muted-foreground text-sm">
          Whole company · booked basis · last {days.length} days
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi
          label={`${days.length}-day booked revenue`}
          value={formatGbpCompact(totalRevenue(data))}
        />
        {/* Counts what the endpoint returned, not what survived projection,
            so the KPI can't quietly undercount the sends in the window. */}
        <Kpi
          label="Campaigns sent"
          value={String(campaignMarkers.length)}
          sub={
            unplotted.length > 0
              ? `in this window · ${unplotted.length} unmarked`
              : "in this window"
          }
        />
      </div>

      <div className="border-border bg-surface rounded-lg border p-4">
        <ChartLegend />
        <ResponsiveContainer width="100%" height={360}>
          <ComposedChart
            data={data}
            margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
          >
            <CartesianGrid stroke="var(--color-border)" vertical={false} />
            {markers.map((m) => (
              <ReferenceLine
                key={m.campaignId}
                x={m.label}
                stroke={MARKER_COLOR}
                strokeDasharray="4 3"
              />
            ))}
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: "var(--color-muted-foreground)" }}
              axisLine={{ stroke: "var(--color-border)" }}
              tickLine={false}
              interval="preserveStartEnd"
              minTickGap={24}
            />
            <YAxis
              domain={[0, revenueMax]}
              allowDataOverflow
              tickCount={5}
              tickFormatter={formatGbpCompact}
              tick={{ fontSize: 10, fill: "var(--color-muted-foreground)" }}
              axisLine={false}
              tickLine={false}
              width={52}
            />
            <Tooltip
              cursor={{ strokeDasharray: "3 3" }}
              content={CampaignImpactTooltip as never}
            />
            <Line
              dataKey="revenue"
              name="Revenue"
              stroke={REVENUE_COLOR}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
        <p className="text-muted-foreground mt-3 text-xs">
          Dashed markers show when a campaign actually sent (its earliest
          executed touch; skipped contacts don't count, a failed send still
          does). A revenue peak shortly after a marker is the signal to look for
          — this chart doesn't compute causation, it just lines the two up.
        </p>
        {unplotted.length > 0 ? (
          <p className="text-muted-foreground mt-2 text-xs">
            {unplotted.length} campaign
            {unplotted.length === 1 ? "" : "s"} in this window{" "}
            {unplotted.length === 1 ? "is" : "are"} not marked (
            {unplotted.map((m) => m.campaign_name).join(", ")}): there was
            neither order intake nor invoicing on the send day, so that day has
            no point on the trend to attach a marker to.
          </p>
        ) : null}
        {markers.length > 0 ? <CampaignSendList markers={markers} /> : null}
      </div>
    </div>
  );
}

// A readable key for the dashed markers, listed below the chart rather than
// labelled directly on it — inline chart labels overlap into an unreadable
// smear once more than a couple of sends land close together in time.
function CampaignSendList({ markers }: { markers: CampaignMarkerDatum[] }) {
  return (
    <div className="border-border mt-4 border-t pt-3">
      <div className="text-muted-foreground mb-2 text-xs font-medium">
        Campaigns sent in this window
      </div>
      <ul className="grid grid-cols-1 gap-x-4 gap-y-1 text-xs sm:grid-cols-2 lg:grid-cols-3">
        {markers.map((m) => (
          <li key={m.campaignId} className="flex items-baseline gap-2">
            <span className="text-muted-foreground shrink-0 font-mono tabular-nums">
              {m.label}
            </span>
            <span className="text-foreground truncate">{m.campaignName}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ChartLegend() {
  return (
    <div className="text-muted-foreground mb-3 flex flex-wrap items-center gap-4 text-xs">
      <span className="inline-flex items-center gap-1.5">
        <span
          className="inline-block h-0 w-4"
          style={{ borderTop: `3px solid ${REVENUE_COLOR}` }}
        />
        Booked revenue
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span
          className="inline-block h-0 w-4"
          style={{ borderTop: `2px dashed ${MARKER_COLOR}` }}
        />
        Campaign sent
      </span>
    </div>
  );
}

function Kpi({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="border-border bg-surface rounded-lg border p-3">
      <div className="text-muted-foreground text-xs font-medium">{label}</div>
      <div className="text-foreground mt-1.5 font-mono text-xl font-semibold tabular-nums">
        {value}
      </div>
      {sub ? (
        <div className="text-muted-foreground mt-0.5 text-xs">{sub}</div>
      ) : null}
    </div>
  );
}

function CampaignImpactTooltip(props: TooltipContentProps<number, string>) {
  if (!props.active || !props.payload || props.payload.length === 0) {
    return null;
  }
  const datum = props.payload[0]?.payload as DailyRevenueDatum | undefined;
  if (!datum) return null;
  return (
    <div
      className="border-border bg-popover text-popover-foreground rounded-lg border px-3 py-2 text-xs shadow-lg"
      role="tooltip"
    >
      <div className="mb-1 font-semibold">
        {datum.label}{" "}
        <span className="text-muted-foreground">{datum.weekday}</span>
      </div>
      <div className="flex items-center justify-between gap-4">
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-0 w-3"
            style={{ borderTop: `2px solid ${REVENUE_COLOR}` }}
          />
          Revenue
        </span>
        <span className="font-mono tabular-nums">
          {formatGbpCompact(datum.revenue)}
        </span>
      </div>
    </div>
  );
}
